import React, { useEffect, useState, FC } from 'react'
import { useIntl } from 'react-intl'
import { useFormContext } from 'react-hook-form'
import { css } from '@emotion/css'
import { Text } from '@contrast-security-inc/design-system-typography'
import { ConfirmationDialog } from '../../../../legacy-design-system/modal'
import { Radio } from '@contrast-security-inc/design-system-inputs'
import { TextInput } from '../../../../legacy-design-system/form-text'
import { JiraIntegrationForm } from './jira-form-schema'
import { JiraFormFieldTypes } from './jira-form-field-types'

export interface CredentialsUpdateConfirmationDialogProps {
  isOpen: boolean
  cancelSubmit: () => void
  submit: (newCredentialSetName?: string) => void
}

export const CredentialsUpdateConfirmationDialog: FC<
  CredentialsUpdateConfirmationDialogProps
> = ({ isOpen, cancelSubmit, submit }) => {
  const { formatMessage } = useIntl()
  const { watch } = useFormContext<JiraIntegrationForm>()

  const [overrideCredentials, setOverrideCredentials] = useState(true)
  const [newCredentialsName, setNewCredentialsName] = useState<string>()
  const credentialsName = watch(JiraFormFieldTypes.CREDENTIALS_NAME)
  useEffect(() => {
    setNewCredentialsName(credentialsName)
  }, [credentialsName])

  return (
    <ConfirmationDialog
      isOpen={isOpen}
      size="medium"
      onCancel={cancelSubmit}
      onConfirm={() =>
        submit(overrideCredentials ? undefined : newCredentialsName)
      }
      confirmButtonProps={{
        disabled: !overrideCredentials && !newCredentialsName?.length
      }}
      renderTitle={() =>
        formatMessage({ id: 'title_integrationsSettings_credentialsActions' })
      }
      renderBody={() => (
        <div>
          <Text as="p">
            {formatMessage({
              id: 'detail_integrationsSettings_credentialsChangeText'
            })}
          </Text>
          <Radio
            label={formatMessage({
              id: 'detail_integrationsSettings_overrideCredentials'
            })}
            isChecked={overrideCredentials}
            onChange={() => setOverrideCredentials(true)}
            testId="jira-form-confirm-credentials-override-radio-button"
          />
          <Text
            className={css`
              margin-left: 24px;
              margin-bottom: 20px;
            `}
            color="neutral60"
            font="metadataStrong"
          >
            {formatMessage({
              id: 'detail_integrationsSettings_overrideCredentialsWarning'
            })}
          </Text>
          <Radio
            label={formatMessage({
              id: 'detail_integrationsSettings_saveNewCredentials'
            })}
            isChecked={!overrideCredentials}
            onChange={() => setOverrideCredentials(false)}
            testId="jira-form-confirm-credentials-new-radio-button"
          />
          <TextInput
            classNameFormTextSecondaryContainer={css`
              margin-left: 24px;
            `}
            data-testid="jira-form-confirm-credentials-new-text-input"
            disabled={overrideCredentials}
            value={newCredentialsName}
            placeholder={formatMessage({
              id: 'placeholder_integrationsSettings_credentialsName'
            })}
            onChange={setNewCredentialsName}
          />
        </div>
      )}
      renderConfirmMessage={() => formatMessage({ id: 'action_continue' })}
      renderCancelMessage={() => formatMessage({ id: 'action_cancel' })}
    />
  )
}
